// Lib
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

// Utils
import { isCommentThread } from '../../../common/elements/utils/elementTypeUtils';
import { isLocationAttached } from '../../../common/elements/utils/elementLocationUtils';
import { getAttachMode, getXPosition, getYPosition } from '../../../common/elements/utils/elementPropertyUtils';

// Selectors
import { getElement } from '../selectors/elementSelector';

// Components
import Element from '../Element';
import CommentAttachedInline from './CommentAttachedInline';

// Types
import { AttachMode } from '../../../common/elements/elementModelTypes';

type CommentAttachedProps = {
    currentBoardId: string;
    elementId: string;
    gridSize: number;
    permissions: number;
    isPresentational: boolean;
    popupCssSelectorPrefix?: string;
};

const CommentAttached = (props: CommentAttachedProps) => {
    const { currentBoardId, elementId, gridSize, permissions, isPresentational, popupCssSelectorPrefix } = props;

    const element = useSelector((state) => getElement(state, { elementId }));
    const x = getXPosition(element);
    const y = getYPosition(element);

    // NOTE - An attached comment's attach mode should never change, so we should be able to safely
    //  use different hooks here.
    const elementPosition = useMemo(
        () => ({
            top: `clamp(0%, ${y}, 100%)`,
            left: `clamp(0%, ${x}, 100%)`,
        }),
        [x, y],
    );

    if (!isCommentThread(element) || !isLocationAttached(element)) return null;

    // No message bubble is shown for inline comments
    if (getAttachMode(element) === AttachMode.inline) {
        return (
            <CommentAttachedInline
                elementId={elementId}
                element={element}
                currentBoardId={currentBoardId}
                permissions={permissions}
                popupCssSelectorPrefix={popupCssSelectorPrefix}
            />
        );
    }

    return (
        <div className="CommentAttached HotSpot" style={elementPosition}>
            <Element
                // @ts-ignore - Can't infer props from JSX Element
                currentBoardId={currentBoardId}
                permissions={permissions}
                elementId={elementId}
                isPresentational={isPresentational}
                element={element}
                gridSize={gridSize}
            />
        </div>
    );
};

export default CommentAttached;
